/**
 * Role-based access for the merchant dashboard.
 *
 * A user holds a role (built-in or a tenant's custom role) plus any extras an
 * owner or admin granted them on the Users page. Auth works out the full set
 * and returns it with the user; services read it from the access token.
 *
 * The keys, role defaults and token form live in `permission-core.ts`, which
 * is copied verbatim into every service. This file adds what the dashboard
 * draws: the permissions laid out like the sidebar.
 */
import {
  LEGACY_KEYS,
  PERMISSION_KEYS,
  ROLE_PERMISSIONS,
  expandPermissions,
  upgradeLegacyPermissions,
  type PermissionKey,
} from './permission-core';

export * from './permission-core';

/** Roles a tenant user can hold. `employee` is shown as "Cashier". */
export type TenantRole = 'owner' | 'admin' | 'manager' | 'employee' | 'inventory_manager';

export const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  employee: 'Cashier',
  inventory_manager: 'Inventory Manager',
};

export function roleLabel(role: string | null | undefined): string {
  if (!role) return '';
  return ROLE_LABELS[role] ?? role;
}

export interface PermissionAction {
  key: PermissionKey;
  /** What ticking it allows, e.g. "Pause, resume and change". */
  label: string;
  /** Sends money or spends message credits. */
  movesMoney?: boolean;
}

/** One screen of a sidebar tab, as a row: View, Create, Edit, Delete, then any extra actions. */
export interface PermissionScreen {
  label: string;
  hint: string;
  /** Sub-heading inside the tab, e.g. "Customer Management". */
  group?: string;
  view?: PermissionAction;
  create?: PermissionAction;
  edit?: PermissionAction;
  delete?: PermissionAction;
  more?: PermissionAction[];
  /** Shown under the row. */
  note?: string;
  /** Not a permission: open by role level only. */
  lockedTo?: 'owner' | 'admin' | 'everyone';
}

export interface PermissionSection {
  id: string;
  label: string;
  screens: PermissionScreen[];
}

const a = (key: PermissionKey, label: string, movesMoney?: boolean): PermissionAction =>
  movesMoney ? { key, label, movesMoney } : { key, label };

/** Every permission, by sidebar tab, in sidebar order. */
export const PERMISSION_SECTIONS: readonly PermissionSection[] = [
  {
    id: 'users',
    label: 'Users',
    screens: [
      {
        label: 'Users',
        hint: 'Staff accounts, their locations and role',
        view: a('users.view', 'See users'),
        create: a('users.create', 'Add users'),
        edit: a('users.edit', 'Change details, locations and role'),
        delete: a('users.deactivate', 'Deactivate and reactivate'),
        note: 'Can give only roles with no more access than their own, and never changes Owners or Admins.',
      },
      { label: 'Roles and extra permissions', hint: 'Custom roles and per-user extras', lockedTo: 'admin' },
    ],
  },
  {
    id: 'receive-payments',
    label: 'Receive Payments',
    screens: [
      {
        label: 'New Sale',
        hint: 'Card and bank payments',
        create: a('rp.sale.create', 'Take a payment'),
        more: [a('rp.sale.schedule', 'Schedule and recurring')],
      },
      {
        label: 'Pay by Link',
        hint: 'Payment links',
        view: a('rp.links.view', 'See payment links'),
        create: a('rp.links.create', 'Create payment links'),
      },
      {
        label: 'Recurring & Scheduled',
        hint: 'Recurring sales and recurring links',
        view: a('rp.recurring.view', 'See schedules'),
        edit: a('rp.recurring.edit', 'Pause, resume and change'),
        delete: a('rp.recurring.cancel', 'Cancel schedules'),
      },
      {
        label: 'Invoices',
        hint: 'Customer invoices',
        view: a('rp.invoices.view', 'See invoices'),
        create: a('rp.invoices.create', 'Create invoices'),
        edit: a('rp.invoices.edit', 'Edit drafts'),
        delete: a('rp.invoices.delete', 'Delete drafts'),
        more: [
          a('rp.invoices.send', 'Send and mark pending'),
          a('rp.invoices.void', 'Void'),
          a('rp.invoices.payment', 'Record payment'),
        ],
      },
      {
        label: 'Customers',
        hint: 'Payers with their cards and bank accounts',
        view: a('rp.customers.view', 'See customers'),
        create: a('rp.customers.create', 'Add customers'),
        edit: a('rp.customers.edit', 'Edit, add cards and banks, set primary'),
        delete: a('rp.customers.delete', 'Deactivate, remove cards and banks'),
      },
      {
        label: 'Transactions',
        hint: 'Last 30 days, ACH authorisation documents',
        view: a('rp.transactions.view', 'See transactions'),
        more: [
          a('rp.transactions.refund', 'Refund', true),
          a('rp.transactions.cancel', 'Cancel'),
          a('rp.transactions.resubmit', 'Resubmit returned ACH'),
        ],
      },
      { label: 'Funding Summary', hint: 'Deposits by day', view: a('rp.funding.view', 'See funding summary') },
    ],
  },
  {
    id: 'pos-management',
    label: 'POS Management',
    screens: [
      {
        group: 'Catalog',
        label: 'Products',
        hint: 'Product list and product form',
        view: a('products.view', 'See products'),
        create: a('products.add', 'Create and duplicate'),
        edit: a('products.edit', 'Change saved products'),
        delete: a('products.delete', 'Deactivate, restore, delete'),
        more: [
          a('products.prices', 'Change prices'),
          a('products.barcodes', 'Change barcodes'),
          a('products.vendors', 'Vendors on products'),
          a('vendors.add', 'Add new vendors'),
          a('products.import', 'Import'),
          a('products.export', 'Export'),
        ],
      },
      {
        group: 'Catalog',
        label: 'Departments',
        hint: 'Including reorder, images and applying defaults',
        view: a('departments.view', 'See departments'),
        create: a('departments.add', 'Create'),
        edit: a('departments.edit', 'Edit'),
        delete: a('departments.delete', 'Delete'),
      },
      {
        group: 'Catalog',
        label: 'Categories',
        hint: 'Including reorder and images',
        view: a('categories.view', 'See categories'),
        create: a('categories.add', 'Create'),
        edit: a('categories.edit', 'Edit'),
        delete: a('categories.delete', 'Delete'),
      },
      {
        group: 'Catalog',
        label: 'Modifiers',
        hint: 'Modifier groups and options',
        view: a('modifiers.view', 'See modifiers'),
        create: a('modifiers.add', 'Create'),
        edit: a('modifiers.edit', 'Edit'),
        delete: a('modifiers.delete', 'Delete'),
      },
      {
        group: 'Catalog',
        label: 'Promotions',
        hint: 'Edit includes start and stop',
        view: a('promotions.view', 'See promotions'),
        create: a('promotions.create', 'Create and duplicate'),
        edit: a('promotions.edit', 'Edit, start, stop'),
        delete: a('promotions.delete', 'Delete'),
      },
      {
        group: 'Catalog',
        label: 'Favorites',
        hint: 'Register favorites layout',
        view: a('favorites.view', 'See layout'),
        edit: a('favorites.edit', 'Change layout'),
      },
      {
        group: 'Orders & stock',
        label: 'Purchase Orders',
        hint: 'Including print',
        view: a('purchase_orders.view', 'See and print'),
        create: a('purchase_orders.create', 'Create'),
        edit: a('purchase_orders.edit', 'Edit, mark paid'),
        delete: a('purchase_orders.delete', 'Delete drafts'),
        more: [
          a('purchase_orders.send', 'Send to vendor'),
          a('purchase_orders.receive', 'Mark received'),
          a('purchase_orders.cancel', 'Cancel'),
        ],
      },
      {
        group: 'Orders & stock',
        label: 'Sale Orders',
        hint: 'Warehouse locations only, including warehouse orders',
        view: a('sales_orders.view', 'See and print pick lists'),
        edit: a('sales_orders.edit', 'Edit quantities'),
        more: [a('sales_orders.fulfil', 'Mark fulfilled'), a('sales_orders.packing', 'Packing lists')],
      },
      {
        group: 'Customer Management',
        label: 'Customers',
        hint: 'Including purchase history, type and credit limit',
        view: a('customers.view', 'See customers'),
        create: a('customers.create', 'Add customers'),
        edit: a('customers.edit', 'Edit customers'),
      },
      {
        group: 'Customer Management',
        label: 'Coupons',
        hint: 'Delete deactivates',
        view: a('coupons.view', 'See coupons'),
        create: a('coupons.create', 'Create coupons'),
        delete: a('coupons.deactivate', 'Deactivate'),
      },
      {
        group: 'Customer Management',
        label: 'Reward Programs',
        hint: 'Delete deactivates',
        view: a('rewards.view', 'See programs'),
        create: a('rewards.create', 'Create programs'),
        delete: a('rewards.deactivate', 'Deactivate'),
      },
      {
        group: 'Customer Management',
        label: 'Campaigns',
        hint: 'SMS and email campaigns',
        view: a('campaigns.view', 'See campaigns'),
        create: a('campaigns.create', 'Draft campaigns'),
        delete: a('campaigns.delete', 'Delete'),
        more: [a('campaigns.send', 'Send and schedule', true), a('campaigns.cancel', 'Cancel sending')],
      },
      {
        group: 'Setup',
        label: 'POS Settings',
        hint: 'Registers, store settings, register role permissions',
        lockedTo: 'owner',
      },
      { group: 'Setup', label: 'Mobile Settings', hint: 'Mobile app permissions', lockedTo: 'admin' },
    ],
  },
  {
    id: 'send-payments',
    label: 'Send Payments',
    screens: [
      {
        label: 'Payees & Vendors',
        hint: 'Who you pay',
        view: a('sp.payees.view', 'See payees'),
        create: a('sp.payees.create', 'Add payees'),
        edit: a('sp.payees.edit', 'Edit payees'),
        delete: a('sp.payees.delete', 'Delete payees'),
      },
      { label: 'Send Payment', hint: 'One-time payments', create: a('sp.payments.send', 'Send a payment', true) },
      {
        label: 'Recurring & Scheduled',
        hint: 'Scheduled and recurring payments',
        view: a('sp.recurring.view', 'See schedules'),
        create: a('sp.recurring.create', 'Schedule payments', true),
        edit: a('sp.recurring.edit', 'Pause, resume and change', true),
        delete: a('sp.recurring.cancel', 'Cancel schedules'),
      },
      {
        label: 'Vendor Invoices',
        hint: 'Upload, check and approve bills',
        view: a('sp.invoices.view', 'See and download'),
        create: a('sp.invoices.upload', 'Upload invoices'),
        edit: a('sp.invoices.edit', 'Edit lines, link POs, attachments'),
        more: [
          a('sp.invoices.approve', 'Approve and reject'),
          a('sp.invoices.inventory', 'Update and revert inventory'),
          a('sp.invoices.autopay', 'Turn on auto-pay', true),
        ],
      },
      {
        label: 'Bills',
        hint: 'Approved bills',
        view: a('sp.bills.view', 'See bills'),
        more: [a('sp.bills.pay', 'Pay bills', true)],
      },
      { label: 'Transactions', hint: 'Outgoing payments', view: a('sp.transactions.view', 'See transactions') },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    screens: [
      {
        label: 'Receive Payments reports',
        hint: 'KPIs and transaction history',
        view: a('reports.rp.view', 'See reports'),
        more: [a('reports.rp.export', 'Export')],
      },
      {
        label: 'POS reports',
        hint: 'Which cards show is still set per location in Report settings',
        view: a('reports.pos.view', 'See reports'),
        more: [
          a('reports.pos.export', 'Export'),
          a('reports.pos.refunded', 'Mark card attempts refunded'),
          a('reports.pos.credit', 'Record credit account payments'),
        ],
      },
      {
        label: 'Send Payments reports',
        hint: 'KPIs and transaction history',
        view: a('reports.sp.view', 'See reports'),
        more: [a('reports.sp.export', 'Export')],
      },
      { label: 'Report settings', hint: 'Which role sees which report, per location', lockedTo: 'owner' },
    ],
  },
  {
    id: 'transactions',
    label: 'Transactions',
    screens: [
      {
        label: 'Funding transactions',
        hint: 'Money in and out of the bank account',
        view: a('transactions.view', 'See transactions'),
      },
    ],
  },
  {
    id: 'reconciliation',
    label: 'Reconciliation',
    screens: [
      {
        label: 'Card reconciliation',
        hint: 'SuperNova and other POS',
        view: a('reconciliation.card.view', 'See reconciliation'),
        more: [a('reconciliation.upload', 'Upload workbooks')],
      },
      {
        label: 'Cash reconciliation',
        hint: 'Daily cash records',
        view: a('reconciliation.cash.view', 'See cash records'),
        edit: a('reconciliation.cash.edit', 'Enter actual cash and notes'),
      },
    ],
  },
  {
    id: 'statements',
    label: 'Statements',
    screens: [
      {
        label: 'Statements',
        hint: 'ACH, card, POS and campaign statements',
        view: a('statements.view', 'Generate and download'),
      },
      { label: 'Statement settings', hint: 'Which role sees which statement', lockedTo: 'owner' },
    ],
  },
  {
    id: 'shelf-labels',
    label: 'Shelf Labels',
    screens: [
      {
        label: 'Labels',
        hint: 'Electronic shelf labels',
        view: a('esl.labels.view', 'See labels'),
        create: a('esl.labels.create', 'Import and link labels'),
        edit: a('esl.labels.edit', 'Relink, locate, send test'),
        more: [a('esl.labels.export', 'Export')],
      },
      {
        label: 'Gateways',
        hint: 'Label gateways',
        view: a('esl.gateways.view', 'See gateways'),
        create: a('esl.gateways.create', 'Register gateways'),
      },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    screens: [
      {
        label: 'Business settings and modules',
        hint: 'Business information, turning modules on and off',
        lockedTo: 'admin',
      },
      { label: 'Online store', hint: 'Online store setup', lockedTo: 'owner' },
      { label: 'Their own settings', hint: 'Home page and two-step sign-in', lockedTo: 'everyone' },
    ],
  },
];

/** A screen's keys: View, Create, Edit, Delete, then its extra actions. */
export function screenKeys(screen: PermissionScreen): PermissionKey[] {
  return [screen.view, screen.create, screen.edit, screen.delete, ...(screen.more ?? [])]
    .filter((x): x is PermissionAction => !!x)
    .map((x) => x.key);
}

/** Every key under a sidebar tab. */
export function sectionKeys(id: string): PermissionKey[] {
  return PERMISSION_SECTIONS.find((s) => s.id === id)?.screens.flatMap(screenKeys) ?? [];
}

/** Every screen's View: sees every tab without changing anything. */
export const VIEW_ONLY_KEYS: readonly PermissionKey[] = PERMISSION_SECTIONS.flatMap((section) =>
  section.screens.flatMap((screen) => (screen.view ? [screen.view.key] : [])),
);

/** Each key's screen and action, e.g. "Invoices: Void". */
export const PERMISSION_LABELS: Readonly<Record<PermissionKey, string>> = Object.fromEntries(
  PERMISSION_SECTIONS.flatMap((section) =>
    section.screens.flatMap((screen) =>
      [screen.view, screen.create, screen.edit, screen.delete, ...(screen.more ?? [])]
        .filter((x): x is PermissionAction => !!x)
        .map((x) => [x.key, `${screen.label}: ${x.label}`]),
    ),
  ),
) as Record<PermissionKey, string>;

/**
 * Effective permissions for a user. The set auth returned for them decides when
 * it's there (it covers custom roles); one from an auth that predates this
 * layout still carries old keys and is upgraded for their role. Without it:
 * super admins get everything, and an unknown role gets only its extras, so a
 * role this build doesn't know never gains access by default.
 */
export function effectivePermissions(
  role: string | null | undefined,
  extras: readonly string[] | null | undefined,
  tokenPermissions?: readonly string[] | null,
): Set<PermissionKey> {
  if (role === 'super_admin') return new Set(PERMISSION_KEYS);
  if (tokenPermissions) {
    const legacy = tokenPermissions.some((k) => Object.prototype.hasOwnProperty.call(LEGACY_KEYS, k));
    return new Set(
      legacy ? upgradeLegacyPermissions(tokenPermissions, role ?? '') : expandPermissions(tokenPermissions),
    );
  }
  return new Set(expandPermissions([...(ROLE_PERMISSIONS[role ?? ''] ?? []), ...(extras ?? [])]));
}

/** True when the user has at least one of the given permissions. */
export function hasAnyPermission(
  role: string | null | undefined,
  extras: readonly string[] | null | undefined,
  keys: readonly PermissionKey[],
  tokenPermissions?: readonly string[] | null,
): boolean {
  const perms = effectivePermissions(role, extras, tokenPermissions);
  return keys.some((k) => perms.has(k));
}
